import type { ApiErrorBody } from "./types";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, body: ApiErrorBody) {
    super(body.error || "Ошибка запроса");
    this.name = "ApiError";
    this.status = status;
    this.code = body.code ?? "unknown";
    this.details = body.details;
  }
}

/** Сообщает приложению, что сессия закончилась, чтобы оно ушло на экран входа. */
type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler = () => {};

export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  onUnauthorized = handler;
}

const CSRF_COOKIE_NAME = "ft_csrf";
const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

/**
 * Признак вкладки для защиты от подделки межсайтовых запросов.
 *
 * Сервер кладёт его в отдельную cookie, доступную скриптам, и ждёт обратно
 * заголовком: cookie сессии браузер отправит и по требованию чужой страницы,
 * а прочитать её и повторить значение в заголовке чужой сайт не сможет.
 */
function csrfToken(): string {
  const prefix = `${CSRF_COOKIE_NAME}=`;
  const found = document.cookie.split("; ").find(part => part.startsWith(prefix));
  return found ? decodeURIComponent(found.slice(prefix.length)) : "";
}

/**
 * Случайный UUID там, где `crypto.randomUUID` недоступен.
 *
 * Он появился в браузерах только в 2021 году и работает лишь в защищённом
 * контексте. На кухонном планшете со старым webview его нет, и заказ не
 * берётся вовсе. То же самое ждёт установку на мини-ПК: панель там открывается
 * по адресу вида `http://192.168.1.10`, а это не защищённый контекст ни в одном
 * браузере, каким бы новым он ни был.
 *
 * `getRandomValues` есть и там, и там: ограничение защищённого контекста
 * касается `randomUUID` и `subtle`, но не его.
 */
function randomUuid(): string {
  const api = typeof crypto === "undefined" ? undefined : crypto;

  if (typeof api?.randomUUID === "function") return api.randomUUID();

  if (typeof api?.getRandomValues === "function") {
    const bytes = api.getRandomValues(new Uint8Array(16));
    // Версия и вариант — как требует RFC 4122 для UUID v4.
    bytes[6] = (bytes[6]! & 0x0f) | 0x40;
    bytes[8] = (bytes[8]! & 0x3f) | 0x80;

    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
    return [
      hex.slice(0, 8),
      hex.slice(8, 12),
      hex.slice(12, 16),
      hex.slice(16, 20),
      hex.slice(20)
    ].join("-");
  }

  /*
   * Совсем древний браузер. Ключ выходит предсказуемым, но от него требуется
   * не тайна, а неповторимость: он отличает повтор одной и той же попытки от
   * второй операции. Отказать здесь значило бы не дать оформить заказ вообще.
   */
  const random = () => Math.random().toString(16).slice(2, 10);
  return `${random()}-${random().slice(0, 4)}-4${random().slice(0, 3)}-a${random().slice(0, 3)}-${random()}${random().slice(0, 4)}`;
}

/**
 * Ключ идемпотентности попытки (п. 30 ТЗ).
 *
 * Ключ рождается на каждый вызов, а не на каждый повтор: браузер сам
 * переотправляет запрос при обрыве соединения, и сервер по одинаковому ключу
 * поймёт, что это та же самая операция, а не вторая.
 */
function newIdempotencyKey(): string {
  return randomUuid();
}

interface RequestOptions extends RequestInit {
  /** Ключ идемпотентности; ставится на операции, которые нельзя повторить дважды. */
  idempotencyKey?: string;
}

async function request<T>(path: string, init: RequestOptions = {}): Promise<T> {
  const { idempotencyKey, ...rest } = init;
  const method = (rest.method ?? "GET").toUpperCase();

  // Content-Type ставится только когда тело действительно есть: заголовок
  // `application/json` при пустом теле Fastify считает ошибкой запроса,
  // а действия вроде «забрать заказ» тела не имеют.
  const hasJsonBody = rest.body !== undefined && rest.body !== null && !(rest.body instanceof FormData);

  const headers: Record<string, string> = { ...(rest.headers as Record<string, string> | undefined) };
  if (hasJsonBody) headers["Content-Type"] = "application/json";
  if (!SAFE_METHODS.has(method)) {
    const token = csrfToken();
    if (token) headers["X-CSRF-Token"] = token;
  }
  if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey;

  const response = await fetch(path, { credentials: "same-origin", ...rest, headers });

  if (response.status === 401) {
    onUnauthorized();
    throw new ApiError(401, { error: "Нужно войти заново", code: "unauthorized" });
  }

  if (response.status === 204) return undefined as T;

  const body = await response.json().catch(() => ({}) as ApiErrorBody);
  if (!response.ok) throw new ApiError(response.status, body as ApiErrorBody);

  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  /** Замена ресурса целиком — например, техкарты, которая правится как одно целое. */
  put: <T>(path: string, body: unknown) => request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  upload: <T>(path: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<T>(path, { method: "POST", body: form });
  },

  /**
   * Операции с деньгами и статусами заказа: повтор такого запроса обязан
   * вернуть прежний результат, а не выполнить действие второй раз.
   */
  once: {
    post: <T>(path: string, body?: unknown) =>
      request<T>(path, {
        method: "POST",
        body: body === undefined ? undefined : JSON.stringify(body),
        idempotencyKey: newIdempotencyKey()
      }),
    patch: <T>(path: string, body: unknown) =>
      request<T>(path, {
        method: "PATCH",
        body: JSON.stringify(body),
        idempotencyKey: newIdempotencyKey()
      })
  }
};
